import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Building2, MapPin, Calendar, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubcontractSlider() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/subcontract-requests?active_only=true`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching subcontract requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = 350;
      sliderRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse">Caricamento richieste...</div>
        </div>
      </section>
    );
  }

  if (requests.length === 0) {
    return null;
  }

  return (
    <section data-testid="subcontract-section" className="py-16 md:py-24 bg-[#F5F5F7] noise-bg relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#E94560] mb-2 block">
              Per Imprese
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E]">
              Richieste di Subappalto
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex gap-2">
              <button
                onClick={() => scroll('left')}
                className="w-10 h-10 flex items-center justify-center bg-white hover:bg-[#1A1A2E] hover:text-white transition-colors"
                data-testid="subcontract-slider-prev"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => scroll('right')}
                className="w-10 h-10 flex items-center justify-center bg-white hover:bg-[#1A1A2E] hover:text-white transition-colors"
                data-testid="subcontract-slider-next"
              >
                <ChevronRight size={20} />
              </button>
            </div>
            <Link
              to="/richieste-subappalto"
              data-testid="view-all-subcontracts-btn"
              className="text-[#E94560] font-bold flex items-center gap-2 hover:underline"
            >
              Vedi tutte
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>

        {/* Slider */}
        <div
          ref={sliderRef}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {requests.map((req) => (
            <Link
              key={req.id}
              to={`/richieste-subappalto/${req.id}`}
              data-testid={`subcontract-card-${req.id}`}
              className="flex-shrink-0 w-[320px] bg-white border-2 border-transparent hover:border-[#E94560] p-6 transition-all snap-start group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#E94560] flex items-center justify-center">
                  <Building2 className="text-white" size={24} />
                </div>
                <span className="text-xs font-bold bg-[#27AE60] text-white px-3 py-1">
                  {req.specialization}
                </span>
              </div>
              <h3 className="text-lg font-bold text-[#1A1A2E] mb-2 group-hover:text-[#E94560] transition-colors line-clamp-2">
                {req.title}
              </h3>
              <p className="text-sm text-[#555555] mb-3">{req.project_name}</p>
              <div className="flex flex-wrap gap-3 text-sm text-[#888888]">
                <span className="flex items-center gap-1">
                  <MapPin size={14} />
                  {req.location}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {req.start_date}
                </span>
              </div>
              <p className="mt-2 text-sm text-[#888888]">Durata: {req.duration}</p>
              {req.budget_range && (
                <p className="mt-2 text-sm font-bold text-[#27AE60]">{req.budget_range}</p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
